import { Skeleton } from "@/components/ui/skeleton";

/**
 * Shared skeleton placeholders for the admin tabs. The tabs use a few
 * recurring layouts:
 *
 *  - `GridSkeleton`  — card-template / sticker thumbnail grids
 *  - `ListSkeleton`  — stacked rows of profile / wishlist / audit items
 *  - `TableSkeleton` — tabular data like contributions / payments
 *
 * Prefer these over the hand-rolled "Loading…" text so the layout
 * reserves the right amount of space while data is in flight. This
 * prevents the "jump" when results arrive and gives the page a
 * perceived-speed boost.
 */

export const GridSkeleton = ({
  count = 12,
  className = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3",
  aspect = "aspect-[3/4]",
}: {
  count?: number;
  className?: string;
  aspect?: string;
}) => (
  <div className={className}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl overflow-hidden border border-border bg-card">
        <Skeleton className={`${aspect} w-full rounded-none`} />
        <div className="p-2 space-y-1.5">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

export const ListSkeleton = ({
  count = 8,
  avatar = false,
}: {
  count?: number;
  avatar?: boolean;
}) => (
  <div className="space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card"
      >
        {avatar && <Skeleton className="h-10 w-10 rounded-full shrink-0" />}
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-2.5 w-1/2" />
        </div>
        <Skeleton className="h-6 w-16 shrink-0" />
      </div>
    ))}
  </div>
);

export const TableSkeleton = ({
  rows = 8,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}) => (
  <div className="rounded-lg border border-border bg-card overflow-hidden">
    <div className="border-b border-border bg-muted/40 px-4 py-3 flex gap-4">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} className="h-3 flex-1" />
      ))}
    </div>
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="border-b border-border last:border-0 px-4 py-3 flex items-center gap-4">
          {Array.from({ length: columns }).map((__, j) => (
            <Skeleton key={j} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const FormSkeleton = ({ fields = 6 }: { fields?: number }) => (
  <div className="space-y-4">
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-1.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    ))}
  </div>
);
